import { createPortal } from "react-dom";
import { Icons } from "./icons";
import type { Subscriber } from "../api";

/**
 * Preview del email transaccional que enviará Sovra Log cuando un incidente
 * se abra o resuelva. Plantilla FIJA por ahora — si más adelante decidimos
 * hacerla editable por organización, este componente recibirá un `template`
 * en vez de tener el HTML hardcodeado.
 *
 * Los datos del trace son MOCK; cuando se mande el correo real, el backend
 * va a sustituir estos placeholders con el incidente real.
 */
interface Props {
  subscriber: Subscriber;
  orgName?: string;
  onClose: () => void;
}

const MOCK_TRACE = {
  service_id: "identidad.renaper",
  service_name: "RENAPER (Keycloak)",
  severity: "major",
  state: "error",
  started_at: "2026-06-04T13:42:08-03:00",
  correlation_id: "467c8e23-c6e8-4705-bd83-64fb55f2857e",
  request: { method: "POST", host: "tst.autenticar.gob.ar", path: "/api/auth/renaper/token" },
  response: { status: 503, status_text: "Service Unavailable" },
  duration_ms: 8200,
  timing: { ttfb_ms: 7900, transfer_ms: 300 },
  error_message: "Upstream returned 503 after 8.2s",
  tags: {
    folio: "TRAM-9421",
    module: "tramites",
    operation: "validar_identidad",
    customer_id_hash: "sha256(••••3942)",
  },
};

export function EmailPreview({ subscriber, orgName, onClose }: Props) {
  return createPortal(
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal-card"
        onClick={(e) => e.stopPropagation()}
        style={{ width: 720, maxWidth: "94vw", maxHeight: "92vh", display: "flex", flexDirection: "column", padding: 0 }}
      >
        {/* Cabecera del modal */}
        <header
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "14px 18px",
            borderBottom: "1px solid var(--border-1)",
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: 16, fontFamily: "var(--font-display)" }}>
              Preview del correo
            </h2>
            <p className="text-sm text-muted" style={{ margin: "2px 0 0" }}>
              Así verá <strong>{subscriber.email}</strong> una notificación cuando un servicio falle.
            </p>
          </div>
          <button type="button" className="btn btn-icon btn-ghost" onClick={onClose} aria-label="Cerrar">
            <Icons.Close size={14} />
          </button>
        </header>

        {/* Metadatos del envío */}
        <div
          style={{
            padding: "10px 18px",
            background: "var(--bg-1)",
            borderBottom: "1px solid var(--border-1)",
            display: "grid",
            gridTemplateColumns: "70px 1fr",
            rowGap: 4,
            fontSize: 12.5,
            fontFamily: "var(--font-mono)",
            color: "var(--fg-2)",
          }}
        >
          <div style={{ color: "var(--fg-3)" }}>From</div>
          <div>notifications@sovra-log.com</div>
          <div style={{ color: "var(--fg-3)" }}>To</div>
          <div>{subscriber.email}</div>
          <div style={{ color: "var(--fg-3)" }}>Subject</div>
          <div>
            🚨 [Sovra Log] {MOCK_TRACE.service_id} respondió {MOCK_TRACE.response.status} — folio{" "}
            {MOCK_TRACE.tags.folio}
          </div>
        </div>

        {/* Cuerpo del email — scrolleable */}
        <div style={{ overflowY: "auto", padding: 18, background: "var(--bg-0)" }}>
          <EmailBody mock={MOCK_TRACE} orgName={orgName ?? "tu organización"} />
        </div>

        {/* Footer con disclaimer */}
        <footer
          style={{
            padding: "10px 18px",
            borderTop: "1px solid var(--border-1)",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontSize: 12,
            color: "var(--fg-3)",
          }}
        >
          <span>
            <Icons.Info size={12} /> Plantilla fija — los datos son de ejemplo
          </span>
          <button type="button" className="btn btn-primary" onClick={onClose}>
            Cerrar
          </button>
        </footer>
      </div>
    </div>,
    document.body,
  );
}

// ── Cuerpo del email ─────────────────────────────────────────────────────────

function EmailBody({ mock, orgName }: { mock: typeof MOCK_TRACE; orgName: string }) {
  // Estilos inline porque un email real va inline para compatibilidad con clientes.
  // Acá mantenemos un look limpio tipo email transaccional.
  return (
    <div
      style={{
        maxWidth: 600,
        margin: "0 auto",
        background: "#ffffff",
        color: "#1a1a1a",
        fontFamily: "system-ui, -apple-system, sans-serif",
        fontSize: 14,
        lineHeight: 1.5,
        borderRadius: 10,
        border: "1px solid #e6e6e6",
        boxShadow: "0 1px 2px rgba(0,0,0,0.04)",
        overflow: "hidden",
      }}
    >
      {/* Header del email */}
      <div
        style={{
          padding: "18px 22px",
          borderBottom: "1px solid #eee",
          display: "flex",
          alignItems: "center",
          gap: 10,
        }}
      >
        <div
          style={{
            width: 28,
            height: 28,
            borderRadius: 6,
            background: "#0a0a0a",
            color: "#fff",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontWeight: 700,
            fontSize: 13,
            fontFamily: "ui-monospace, monospace",
          }}
        >
          SL
        </div>
        <span style={{ fontWeight: 600, fontSize: 14 }}>Sovra Log</span>
      </div>

      {/* Banner severidad */}
      <div
        style={{
          background: "#fff6f6",
          borderLeft: "4px solid #d33",
          padding: "12px 22px",
          margin: "0",
          color: "#a00",
          fontWeight: 600,
          fontSize: 14,
        }}
      >
        🚨 Incidente {mock.severity.toUpperCase()} en {mock.service_name}
      </div>

      {/* Mensaje principal */}
      <div style={{ padding: "20px 22px" }}>
        <p style={{ margin: "0 0 14px" }}>
          Se detectó un fallo en el servicio <strong>{mock.service_id}</strong> que está siendo
          observado en {orgName}.
        </p>

        {/* Resumen del trace */}
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            margin: "14px 0",
            fontSize: 13,
          }}
        >
          <tbody>
            <SummaryRow label="Endpoint">
              <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}>
                {mock.request.method} {mock.request.host}
                {mock.request.path}
              </span>
            </SummaryRow>
            <SummaryRow label="Status">
              <span
                style={{
                  fontFamily: "ui-monospace, monospace",
                  background: "#fee",
                  color: "#a00",
                  padding: "1px 8px",
                  borderRadius: 4,
                  fontWeight: 600,
                }}
              >
                {mock.response.status} {mock.response.status_text}
              </span>
            </SummaryRow>
            <SummaryRow label="Duración">
              <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}>
                {mock.duration_ms} ms · TTFB {mock.timing.ttfb_ms} ms · transfer {mock.timing.transfer_ms} ms
              </span>
            </SummaryRow>
            <SummaryRow label="Hora">
              <span style={{ fontFamily: "ui-monospace, monospace", fontSize: 12 }}>{mock.started_at}</span>
            </SummaryRow>
            <SummaryRow label="Mensaje">
              <span style={{ color: "#a00", fontFamily: "ui-monospace, monospace", fontSize: 12 }}>
                {mock.error_message}
              </span>
            </SummaryRow>
          </tbody>
        </table>

        {/* Tags de negocio */}
        <div
          style={{
            background: "#f8f8f8",
            padding: "12px 16px",
            borderRadius: 6,
            margin: "16px 0",
            fontSize: 12,
            fontFamily: "ui-monospace, monospace",
          }}
        >
          <div style={{ color: "#666", marginBottom: 6, fontWeight: 600 }}>Tags del trace</div>
          {Object.entries(mock.tags).map(([k, v]) => (
            <div key={k} style={{ display: "flex", gap: 8 }}>
              <span style={{ color: "#999", minWidth: 130 }}>{k}</span>
              <span>{v}</span>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div style={{ margin: "22px 0 4px", textAlign: "center" }}>
          <a
            href={`https://sovra-log.example/traces/${mock.correlation_id}`}
            style={{
              display: "inline-block",
              background: "#0a0a0a",
              color: "#fff",
              padding: "10px 22px",
              borderRadius: 6,
              textDecoration: "none",
              fontWeight: 600,
              fontSize: 13.5,
            }}
          >
            Ver trace completo →
          </a>
        </div>

        <p
          style={{
            textAlign: "center",
            marginTop: 10,
            fontSize: 11,
            color: "#888",
            fontFamily: "ui-monospace, monospace",
          }}
        >
          {mock.correlation_id}
        </p>
      </div>

      {/* Footer */}
      <div
        style={{
          padding: "14px 22px",
          borderTop: "1px solid #eee",
          fontSize: 11.5,
          color: "#888",
          textAlign: "center",
          background: "#fafafa",
        }}
      >
        Recibís este correo porque sos suscriptor de Sovra Log para <strong>{orgName}</strong>.
        <br />
        <a href="#" style={{ color: "#0066cc" }}>Configurar mis notificaciones</a>
        {" · "}
        <a href="#" style={{ color: "#0066cc" }}>Darme de baja</a>
      </div>
    </div>
  );
}

function SummaryRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <tr>
      <td
        style={{
          padding: "6px 12px 6px 0",
          color: "#666",
          width: 100,
          verticalAlign: "top",
          fontWeight: 600,
        }}
      >
        {label}
      </td>
      <td style={{ padding: "6px 0", verticalAlign: "top" }}>{children}</td>
    </tr>
  );
}
